import { useState, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../lib/api';
import { BACKEND_URL } from '../../lib/backend';
import { toast } from 'sonner';
import Cropper from 'react-easy-crop';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { Slider } from '../ui/slider';
import {
  User, Loader2, CheckCircle, Shield, AtSign, Calendar, Camera, ZoomIn, RotateCw
} from 'lucide-react';

async function getCroppedBlob(imageSrc, pixelCrop) {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  await new Promise((resolve, reject) => { img.onload = resolve; img.onerror = reject; img.src = imageSrc; });
  const canvas = document.createElement('canvas');
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, pixelCrop.width, pixelCrop.height);
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
}

export function ProfileTab({ profile }) {
  const { refreshUser } = useAuth();
  const fileInputRef = useRef(null);

  const [username, setUsername] = useState(profile?.username || '');
  const [name, setName] = useState(profile?.name || '');
  const [avatarUrl, setAvatarUrl] = useState(profile?.avatar_url || '');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);

  // Crop state
  const [cropImage, setCropImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const onCropComplete = useCallback((_, pixels) => setCroppedAreaPixels(pixels), []);

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.updateProfile({ username, name });
      await refreshUser();
      toast.success('Profile updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    }
    setSavingProfile(false);
  };

  const handleAvatarUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    const reader = new FileReader();
    reader.onload = () => { setCropImage(reader.result); setCrop({ x: 0, y: 0 }); setZoom(1); setRotation(0); };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleCropSave = async () => {
    if (!cropImage || !croppedAreaPixels) return;
    setUploadingAvatar(true);
    try {
      const blob = await getCroppedBlob(cropImage, croppedAreaPixels);
      const formData = new FormData();
      formData.append('file', blob, 'avatar.jpg');
      const res = await api.uploadAvatar(formData);
      setAvatarUrl(res.data.avatar_url);
      await refreshUser();
      setCropImage(null);
      toast.success('Avatar updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload avatar');
    }
    setUploadingAvatar(false);
  };

  return (
    <>
      <div className="bg-surface border border-[#262626] rounded-xl p-6 space-y-6">
        <div>
          <h2 className="text-lg font-semibold mb-1">Profile Information</h2>
          <p className="text-sm text-[#A1A1AA]">Your public identity on Tune4Tune</p>
        </div>

        {/* Avatar */}
        <div className="flex items-center gap-5">
          <div className="relative group">
            <div className="w-20 h-20 rounded-full bg-[#1a1a1a] border-2 border-[#262626] overflow-hidden flex items-center justify-center">
              {avatarUrl ? (
                <img src={`${BACKEND_URL}${avatarUrl}`} alt="Avatar" className="w-full h-full object-cover" />
              ) : (
                <User className="w-8 h-8 text-[#555]" />
              )}
            </div>
            <button onClick={() => fileInputRef.current?.click()} className="absolute inset-0 rounded-full bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center" disabled={uploadingAvatar} data-testid="avatar-upload-btn">
              {uploadingAvatar ? <Loader2 className="w-5 h-5 animate-spin text-white" /> : <Camera className="w-5 h-5 text-white" />}
            </button>
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} />
          </div>
          <div>
            <p className="font-medium">{name || 'Your Name'}</p>
            <p className="text-sm text-[#A1A1AA]">{username ? `@${username}` : 'No username set'}</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <Label htmlFor="username" className="flex items-center gap-2 mb-2"><AtSign className="w-4 h-4 text-lime" />Public Username</Label>
            <Input id="username" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Choose a unique username" className="bg-[#111] border-[#262626] max-w-md" maxLength={30} data-testid="input-username" />
            <p className="text-xs text-[#666] mt-1">Visible on your orders, messages, and reviews. 3-30 characters.</p>
          </div>
          <div>
            <Label htmlFor="name" className="flex items-center gap-2 mb-2"><User className="w-4 h-4 text-lime" />Display Name</Label>
            <Input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your display name" className="bg-[#111] border-[#262626] max-w-md" data-testid="input-name" />
          </div>
          <div className="flex items-center gap-3 p-3 bg-[#0a0a0a] rounded-lg border border-[#1a1a1a]">
            <Shield className="w-4 h-4 text-[#555]" />
            <p className="text-sm text-[#A1A1AA]">Email: <span className="text-white">{profile?.email}</span></p>
          </div>
          <div className="flex items-center gap-3 p-3 bg-[#0a0a0a] rounded-lg border border-[#1a1a1a]">
            <Calendar className="w-4 h-4 text-[#555]" />
            <p className="text-sm text-[#A1A1AA]">Member since: <span className="text-white">{profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : 'N/A'}</span></p>
          </div>
        </div>

        <Button onClick={handleSaveProfile} disabled={savingProfile} className="bg-lime text-black hover:bg-lime/90 font-bold" data-testid="save-profile-btn">
          {savingProfile ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
          Save Profile
        </Button>
      </div>

      {/* Avatar Crop Dialog */}
      <Dialog open={!!cropImage} onOpenChange={(open) => { if (!open) setCropImage(null); }}>
        <DialogContent className="bg-[#0a0a0a] border-[#262626] max-w-lg p-0 overflow-hidden">
          <DialogTitle className="sr-only">Crop Avatar</DialogTitle>
          <div className="p-4 border-b border-[#262626]">
            <h3 className="font-semibold text-lg">Crop Avatar</h3>
            <p className="text-sm text-[#A1A1AA]">Drag to reposition, scroll to zoom</p>
          </div>
          <div className="relative w-full h-[340px] bg-[#111]">
            {cropImage && <Cropper image={cropImage} crop={crop} zoom={zoom} rotation={rotation} aspect={1} cropShape="round" showGrid={false} onCropChange={setCrop} onZoomChange={setZoom} onRotationChange={setRotation} onCropComplete={onCropComplete} />}
          </div>
          <div className="p-4 space-y-4 border-t border-[#262626]">
            <div className="flex items-center gap-3">
              <ZoomIn className="w-4 h-4 text-[#666] flex-shrink-0" />
              <Slider value={[zoom]} min={1} max={3} step={0.05} onValueChange={([v]) => setZoom(v)} className="flex-1" data-testid="crop-zoom-slider" />
            </div>
            <div className="flex items-center gap-3">
              <RotateCw className="w-4 h-4 text-[#666] flex-shrink-0" />
              <Slider value={[rotation]} min={0} max={360} step={1} onValueChange={([v]) => setRotation(v)} className="flex-1" data-testid="crop-rotation-slider" />
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setCropImage(null)} className="border-[#333]" data-testid="crop-cancel-btn">Cancel</Button>
              <Button onClick={handleCropSave} disabled={uploadingAvatar} className="bg-lime text-black hover:bg-lime/90 font-bold" data-testid="crop-save-btn">
                {uploadingAvatar ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Save Avatar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
